import classNames from 'classnames'
import { Gift, Plus, Trash2 } from 'lucide-react'
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form'

import { TournamentFeeBreakdown } from 'components/TournamentFeeBreakdown'

import type { Contributor, CreateTournamentInput, PaymentMethod, PrizePosition } from '../types'
import { Banner, ChoiceCards, FOCUS_RING, FieldError, FieldLabel, Help, Section, inputClass } from '../ui'
import { PRIZE_UNIT } from '../wizard'

const PAYMENT_OPTIONS: {
  value: PaymentMethod
  title: string
  description: string
  icon: JSX.Element
  disabled?: boolean
  badge?: string
}[] = [
  {
    value: 'leet_token',
    title: 'LEET',
    description: 'You deposit the prize pool in LEET from your wallet when you create the tournament.',
    icon: <img src="/logo.png" alt="" className="h-5 w-5 object-contain" />,
  },
  {
    value: 'coinbase_usdc',
    title: 'USDC',
    description: 'You deposit USDC on Base into escrow. Winners are paid automatically when it ends.',
    icon: <img src="/assets/usdc-logo.webp" alt="" className="h-5 w-5 object-contain" />,
  },
  {
    value: 'paypal',
    title: 'PayPal',
    description: 'Fund the prize pool with PayPal.',
    icon: <img src="/assets/paypal-logo.webp" alt="" className="h-5 w-5 object-contain" />,
    disabled: true,
    badge: 'Coming soon',
  },
  {
    value: 'free',
    title: 'Free',
    description: 'No prize pool and no deposit. Players compete for bragging rights.',
    icon: <Gift size={16} />,
  },
]

const ordinal = (n: number) => {
  if (n % 10 === 1 && n % 100 !== 11) return 'st'
  if (n % 10 === 2 && n % 100 !== 12) return 'nd'
  if (n % 10 === 3 && n % 100 !== 13) return 'rd'
  return 'th'
}

/** Same labels the old PrizePositionInput produced ("1st Position", "2nd Position", …). */
const relabel = (positions: PrizePosition[]) =>
  positions.map((position, index) => ({ ...position, prize: `${index + 1}${ordinal(index + 1)} Position` }))

const placeLabel = (index: number) => `${index + 1}${ordinal(index + 1)} place`

export type EntryStepProps = {
  form: UseFormReturn<CreateTournamentInput>
  onPaymentMethodChange: (method: PaymentMethod) => void
  positions: PrizePosition[]
  setPositions: (next: PrizePosition[]) => void
  contributors: Contributor[]
  setContributors: (next: Contributor[]) => void
  /** Shown after a failed Next / Create on this step. */
  prizeError: string | null
}

export const EntryStep = ({ form, onPaymentMethodChange, positions, setPositions, contributors, setContributors, prizeError }: EntryStepProps) => {
  const {
    control,
    formState: { errors },
  } = form
  const [paymentMethodRaw, prizePool, maxPlayerSize, playersPerTeam] = useWatch({
    control,
    name: ['payment_method', 'prize_pool', 'max_player_size', 'players_per_team'],
  })
  const paymentMethod = (paymentMethodRaw || 'leet_token') as PaymentMethod
  const isFree = paymentMethod === 'free'
  const unit = PRIZE_UNIT[paymentMethod]

  const positionsTotal = positions.reduce((sum, p) => sum + (parseInt(p.percentage) || 0), 0)
  const creatorsTotal = contributors.reduce((sum, c) => sum + (parseInt(c.percentage) || 0), 0)
  const total = positionsTotal + creatorsTotal

  return (
    <div>
      <Section title="Prize funding" description="Who pays the prize pool, and in what currency.">
        <div data-field="payment_method">
          <Controller
            control={control}
            name="payment_method"
            rules={{ required: 'Choose how the prize pool is funded.' }}
            render={({ field }) => (
              <ChoiceCards
                label="Prize funding"
                columns="grid-cols-1 sm:grid-cols-2"
                options={PAYMENT_OPTIONS}
                value={(field.value || 'leet_token') as PaymentMethod}
                onChange={(v) => {
                  field.onChange(v)
                  onPaymentMethodChange(v)
                }}
              />
            )}
          />
          <FieldError message={errors.payment_method?.message} />
        </div>
      </Section>

      <Section title="Prize pool">
        <div data-field="prize_pool">
          <FieldLabel htmlFor="ct-prize-pool">Amount{unit ? ` (${unit})` : ''}</FieldLabel>
          <Controller
            control={control}
            name="prize_pool"
            rules={{ required: 'Enter the prize pool amount.' }}
            render={({ field }) => (
              <div className="relative max-w-xs">
                <input
                  id="ct-prize-pool"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  placeholder={paymentMethod === 'coinbase_usdc' ? '500' : paymentMethod === 'paypal' ? '1000' : '10000'}
                  aria-invalid={!!errors.prize_pool}
                  aria-describedby={errors.prize_pool ? 'ct-prize-pool-error' : 'ct-prize-pool-help'}
                  className={classNames(inputClass(!!errors.prize_pool), 'pr-16 font-display text-2xl font-bold tabular-nums disabled:cursor-not-allowed disabled:opacity-60')}
                  name={field.name}
                  ref={field.ref}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  value={isFree ? '0' : field.value || ''}
                  disabled={isFree}
                />
                {unit && (
                  <span className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-sm font-semibold text-light-typo-gray dark:text-dark-typo-gray">
                    {unit}
                  </span>
                )}
              </div>
            )}
          />
          {errors.prize_pool ? (
            <FieldError id="ct-prize-pool-error" message={errors.prize_pool.message} />
          ) : (
            <Help id="ct-prize-pool-help">
              {isFree
                ? 'Free tournaments have no prize pool.'
                : paymentMethod === 'coinbase_usdc'
                  ? 'Held in escrow on Base. If you don’t have enough USDC we’ll help you buy it.'
                  : paymentMethod === 'leet_token'
                    ? 'Your wallet will ask you to approve and deposit this amount.'
                    : 'Total paid out to winners.'}
            </Help>
          )}
        </div>
      </Section>

      <Section
        title="Prize split"
        description="Share of the prize pool each place receives. Places and creator shares must add up to 100%."
      >
        <div data-field="prize_positions" className="grid gap-2">
          {positions.map((position, index) => (
            <div key={position.id} className="flex items-center gap-3">
              <label htmlFor={`ct-place-${position.id}`} className="w-24 flex-shrink-0 text-sm font-semibold text-light-typo-primary dark:text-dark-typo-primary">
                {placeLabel(index)}
              </label>
              <div className="relative w-28">
                <input
                  id={`ct-place-${position.id}`}
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="100"
                  placeholder="0"
                  value={position.percentage}
                  onChange={(e) =>
                    setPositions(relabel(positions.map((p) => (p.id === position.id ? { ...p, percentage: e.target.value } : p))))
                  }
                  className={classNames(inputClass(), 'pr-8 text-right tabular-nums')}
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-light-typo-gray dark:text-dark-typo-gray">%</span>
              </div>
              {positions.length > 1 && index === positions.length - 1 && (
                <button
                  type="button"
                  onClick={() => setPositions(relabel(positions.filter((p) => p.id !== position.id)))}
                  className={classNames('rounded-md p-2 text-light-typo-gray dark:text-dark-typo-gray hover:text-red', FOCUS_RING)}
                  aria-label={`Remove ${placeLabel(index)}`}
                >
                  <Trash2 size={16} aria-hidden />
                </button>
              )}
            </div>
          ))}
          {positions.length < 20 && (
            <button
              type="button"
              onClick={() =>
                setPositions(
                  relabel([
                    ...positions,
                    { id: `${positions.length + 1}`, prize: '', percentage: '' },
                  ]),
                )
              }
              className={classNames(
                'mt-1 inline-flex w-fit items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-semibold text-green hover:bg-green/10',
                FOCUS_RING,
              )}
            >
              <Plus size={14} aria-hidden /> Add a place
            </button>
          )}
          <TotalBar total={total} places={positionsTotal} creators={creatorsTotal} />
        </div>
        <div className="mt-3">
          <TournamentFeeBreakdown
            prizePool={prizePool || '0'}
            prizePositions={positions}
            maxPlayerSize={maxPlayerSize}
            playersPerTeam={playersPerTeam}
            paymentMethod={paymentMethod}
            contributors={contributors}
          />
        </div>
      </Section>

      <Section
        title="Content creators"
        description="Optional. Give streamers or casters a share of the prize pool. Their share comes out of the 100%."
      >
        <CreatorsEditor contributors={contributors} setContributors={setContributors} />
      </Section>

      {prizeError && <Banner tone="error">{prizeError}</Banner>}
    </div>
  )
}

const TotalBar = ({ total, places, creators }: { total: number; places: number; creators: number }) => {
  const ok = total === 100
  return (
    <div className="mt-2" aria-live="polite">
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-light-typo-gray dark:text-dark-typo-gray">
          Places {places}%{creators > 0 ? ` · creators ${creators}%` : ''}
        </span>
        <span className={classNames('font-display text-base font-bold tabular-nums', ok ? 'text-green' : total > 100 ? 'text-red' : 'text-yellow')}>
          {total}% of 100%
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-light-border-primary dark:bg-dark-border-primary">
        <div
          className={classNames('h-full rounded-full transition-[width] motion-reduce:transition-none', ok ? 'bg-gradient-to-r from-btn-fancy-primary-start to-btn-fancy-primary-end' : total > 100 ? 'bg-red' : 'bg-yellow')}
          style={{ width: `${Math.min(total, 100)}%` }}
        />
      </div>
    </div>
  )
}

const CreatorsEditor = ({ contributors, setContributors }: { contributors: Contributor[]; setContributors: (next: Contributor[]) => void }) => {
  const update = (index: number, key: keyof Contributor, value: string) =>
    setContributors(contributors.map((c, i) => (i === index ? { ...c, [key]: value } : c)))

  return (
    <div className="grid gap-3">
      {contributors.map((contributor, index) => (
        <fieldset key={index} aria-label={`Creator ${index + 1}`} className="rounded-xl border border-light-border-primary dark:border-dark-border-primary p-3 sm:p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-light-typo-primary dark:text-dark-typo-primary">Creator {index + 1}</span>
            <button
              type="button"
              onClick={() => setContributors(contributors.filter((_, i) => i !== index))}
              className={classNames('inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-light-typo-gray dark:text-dark-typo-gray hover:text-red', FOCUS_RING)}
            >
              <Trash2 size={13} aria-hidden /> Remove
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <FieldLabel htmlFor={`ct-creator-name-${index}`}>Name</FieldLabel>
              <input id={`ct-creator-name-${index}`} className={inputClass()} placeholder="e.g. NightCaster" value={contributor.name} onChange={(e) => update(index, 'name', e.target.value)} />
            </div>
            <div>
              <FieldLabel htmlFor={`ct-creator-role-${index}`}>Role</FieldLabel>
              <input id={`ct-creator-role-${index}`} className={inputClass()} placeholder="Streamer, caster, host…" value={contributor.role} onChange={(e) => update(index, 'role', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel htmlFor={`ct-creator-wallet-${index}`}>Wallet address</FieldLabel>
              <input
                id={`ct-creator-wallet-${index}`}
                className={classNames(inputClass(), 'font-mono text-xs sm:text-sm')}
                placeholder="0x…"
                spellCheck={false}
                autoComplete="off"
                value={contributor.address}
                onChange={(e) => update(index, 'address', e.target.value.trim())}
              />
            </div>
            <div>
              <FieldLabel htmlFor={`ct-creator-share-${index}`}>Share</FieldLabel>
              <div className="relative w-28">
                <input
                  id={`ct-creator-share-${index}`}
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="100"
                  placeholder="0"
                  className={classNames(inputClass(), 'pr-8 text-right tabular-nums')}
                  value={contributor.percentage}
                  onChange={(e) => update(index, 'percentage', e.target.value)}
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-light-typo-gray dark:text-dark-typo-gray">%</span>
              </div>
            </div>
          </div>
        </fieldset>
      ))}
      {contributors.length < 3 && (
        <button
          type="button"
          onClick={() => setContributors([...contributors, { name: '', address: '', role: '', percentage: '' }])}
          className={classNames(
            'flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-light-border-primary dark:border-dark-border-primary px-3 py-3 text-sm font-semibold text-light-typo-gray dark:text-dark-typo-gray hover:border-btn-fancy-primary-end/60 hover:text-light-typo-primary dark:hover:text-dark-typo-primary',
            FOCUS_RING,
          )}
        >
          <Plus size={14} aria-hidden /> Add a creator
        </button>
      )}
    </div>
  )
}
